// The "wow" played once when someone picks the boldest creativity level: a starburst, a rocket with a trail and a
// shower of confetti from the card. It lives in its own layers on top of the page, never blocks input and is
// removed when it ends.

type BoomParticleType = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  ay: number;
  drag: number;
  life: number;
  max: number;
  size: number;
  rot: number;
  vr: number;
  flip: number;
  color: string;
  shape: 'star' | 'rect' | 'dot';
  glow: number;
  fade: boolean;
};

const BOOM_DURATION_MS = 2800;
const ROCKET_DURATION_MS = 1500;
const BURST_PARTICLES = 120;
const COLORS = ['#ff4d8d', '#ffb703', '#4b7cff', '#2dd4bf', '#a855f7', '#ff7a3d'];
const TRAIL_COLORS = ['#ffb703', '#ff4d8d', '#fff3b0'];

const random = (min: number, max: number): number => min + Math.random() * (max - min);
const pick = <T,>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

const drawStar = (context: CanvasRenderingContext2D, size: number) => {
  context.beginPath();
  context.moveTo(0, -size);
  context.quadraticCurveTo(0, 0, size, 0);
  context.quadraticCurveTo(0, 0, 0, size);
  context.quadraticCurveTo(0, 0, -size, 0);
  context.quadraticCurveTo(0, 0, 0, -size);
  context.closePath();
  context.fill();
};

const drawParticle = (context: CanvasRenderingContext2D, particle: BoomParticleType) => {
  const progress = particle.life / particle.max;
  const alpha = particle.fade ? 1 - progress * progress : 1;
  if (alpha <= 0.01) {
    return;
  }
  context.save();
  context.globalAlpha = alpha;
  context.fillStyle = particle.color;
  if (particle.glow) {
    context.shadowColor = particle.color;
    context.shadowBlur = particle.glow;
  }
  context.translate(particle.x, particle.y);
  context.rotate(particle.rot);
  if (particle.shape === 'rect') {
    context.scale(1, Math.cos(particle.life * particle.flip));
    context.fillRect(-particle.size / 2, -particle.size / 3, particle.size, particle.size * 0.66);
  } else if (particle.shape === 'star') {
    drawStar(context, particle.size);
  } else {
    context.beginPath();
    context.arc(0, 0, particle.size, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();
};

const createParticle = (overrides: Partial<BoomParticleType>): BoomParticleType => ({
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  ay: 0,
  drag: 0,
  life: 0,
  max: 1,
  size: 6,
  rot: 0,
  vr: 0,
  flip: 6,
  color: '#fff',
  shape: 'dot',
  glow: 0,
  fade: true,
  ...overrides,
});

/**
 * Plays the effect from the middle of `origin` and returns a function that stops it at once. It does nothing when the
 * person asked for reduced motion or the tab is hidden.
 */
export function playCreativeBoom(origin: DOMRect): () => void {
  if (
    typeof window === 'undefined' ||
    document.hidden ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return () => undefined;
  }

  const originX = origin.left + origin.width / 2;
  const originY = origin.top + origin.height / 2;
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

  const canvas = document.createElement('canvas');
  canvas.className = 'sb-boom-canvas';
  canvas.width = window.innerWidth * pixelRatio;
  canvas.height = window.innerHeight * pixelRatio;
  canvas.setAttribute('aria-hidden', 'true');
  const context = canvas.getContext('2d');

  const wow = document.createElement('div');
  wow.className = 'sb-boom-wow';
  wow.setAttribute('aria-hidden', 'true');
  wow.innerHTML = '<b>WOW!</b>';

  const rocket = document.createElement('div');
  rocket.className = 'sb-boom-rocket';
  rocket.setAttribute('aria-hidden', 'true');
  rocket.textContent = '\u{1F680}';

  document.body.append(canvas, wow, rocket);

  const particles: BoomParticleType[] = [];
  for (let index = 0; index < BURST_PARTICLES; index += 1) {
    const angle = random(0, Math.PI * 2);
    const speed = random(150, 520);
    particles.push(
      createParticle({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        ay: 420,
        drag: 0.8,
        max: random(1, 2),
        size: random(5, 11),
        shape: index % 2 ? 'star' : 'rect',
        color: pick(COLORS),
        vr: random(-8, 8),
        flip: random(4, 9),
        fade: false,
      }),
    );
  }

  let frame = 0;
  let stopped = false;
  const startedAt = performance.now();
  let lastFrameAt = startedAt;

  const stop = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    cancelAnimationFrame(frame);
    canvas.remove();
    wow.remove();
    rocket.remove();
  };

  const tick = (now: number) => {
    if (stopped) {
      return;
    }
    const elapsed = now - startedAt;
    if (elapsed >= BOOM_DURATION_MS || !context) {
      stop();
      return;
    }
    const delta = Math.min(0.05, (now - lastFrameAt) / 1000);
    lastFrameAt = now;

    const rocketProgress = elapsed / ROCKET_DURATION_MS;
    if (rocketProgress < 1) {
      const rocketX = originX + rocketProgress * window.innerWidth * 0.55;
      const rocketY =
        originY - rocketProgress * window.innerHeight * 0.9 - Math.sin(rocketProgress * Math.PI) * 60;
      rocket.style.transform = `translate(${rocketX}px, ${rocketY}px) translate(-50%, -50%)`;
      for (let index = 0; index < 3; index += 1) {
        particles.push(
          createParticle({
            x: rocketX - 8,
            y: rocketY + 14,
            vx: random(-40, 20),
            vy: random(30, 90),
            max: random(0.5, 1),
            size: random(4, 9),
            color: pick(TRAIL_COLORS),
            glow: 10,
          }),
        );
      }
    } else {
      rocket.style.display = 'none';
    }

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, window.innerWidth, window.innerHeight);
    for (let index = particles.length - 1; index >= 0; index -= 1) {
      const particle = particles[index];
      particle.life += delta;
      if (particle.life >= particle.max) {
        particles.splice(index, 1);
        continue;
      }
      particle.vy += particle.ay * delta;
      if (particle.drag) {
        const keep = Math.max(0, 1 - particle.drag * delta);
        particle.vx *= keep;
        particle.vy *= keep;
      }
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      particle.rot += particle.vr * delta;
      drawParticle(context, particle);
    }
    frame = requestAnimationFrame(tick);
  };

  rocket.style.transform = `translate(${originX}px, ${originY}px) translate(-50%, -50%)`;
  frame = requestAnimationFrame(tick);
  return stop;
}
